/**
 * The text read out of an attachment — on its card, and over its picture.
 *
 *   OcrBadge      the card's chip: being read, read, nothing to read, failed
 *   OcrTextPanel  the text itself, paragraphs and tables, selectable, with
 *                 copy — drawn over the picture in the viewer, so the desk
 *                 can read a blurred line against the scan and copy a value
 *
 * The reading happens on the server, in the background, as soon as a
 * picture is filed (Backend/lib/ocrQueue.js) — nobody presses anything.
 */

import { useState } from 'react';
import { Icon } from '../../../components/common';
import { useNotifications } from '../../../context/NotificationContext';
import { OCR_STATUS_META, ocrBlocks, ocrPlainText } from '../../../domain/ocrText';
import { patientsApi } from '../../../api/endpoints';

export function OcrBadge({ row, compact = false }) {
  const meta = OCR_STATUS_META[row && row.ocrStatus];
  if (!meta) return null;
  return (
    <span className={`ocr-badge ocr-badge--${meta.tone}`} title={meta.label}>
      <Icon name={meta.icon} size={12} className={row.ocrStatus === 'processing' ? 'animate-spin' : ''} />
      {!compact && <span>{row.ocrStatus === 'done' && row.ocrHasTable ? 'نص وجداول مستخرجة' : meta.label}</span>}
    </span>
  );
}

export function OcrTextPanel({ row, onClose = null, onReread = null, floating = false }) {
  const { success, error: toastError } = useNotifications();
  const [busy, setBusy] = useState(false);
  const blocks = ocrBlocks(row.ocrText);
  const meta = OCR_STATUS_META[row.ocrStatus];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ocrPlainText(row.ocrText));
      success('تم نسخ النص المستخرج');
    } catch {
      toastError('تعذر النسخ — المتصفح منع الوصول للحافظة');
    }
  };

  const reread = async () => {
    setBusy(true);
    try {
      const updated = await patientsApi.rereadAttachment(row.id);
      success('بدأت قراءة الصورة من جديد في الخلفية');
      onReread?.(updated);
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`ocr-panel ${floating ? 'ocr-panel--floating' : ''}`} aria-label="النص المستخرج من الصورة">
      <header className="ocr-panel__head">
        <span className="ocr-panel__icon" aria-hidden="true">
          <Icon name="scan-text" size={16} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-black">النص المستخرج من الصورة</p>
          <p className="text-[10.5px] opacity-75">
            {meta ? meta.label : ''}
            {row.ocrLanguages ? ` · ${row.ocrLanguages === 'ar+en' ? 'عربي وإنجليزي' : row.ocrLanguages === 'ar' ? 'عربي' : 'إنجليزي'}` : ''}
            {Number.isFinite(row.ocrConfidence) && row.ocrStatus === 'done' ? ` · دقة القراءة ${row.ocrConfidence}%` : ''}
            {' · على خادم المستشفى'}
          </p>
        </div>
        {row.ocrStatus === 'done' && (
          <button type="button" className="ocr-panel__tool" onClick={copy}>
            <Icon name="copy" size={13} /> نسخ
          </button>
        )}
        {onReread && ['done', 'empty', 'failed'].includes(row.ocrStatus) && (
          <button type="button" className="ocr-panel__tool" onClick={reread} disabled={busy} title="قراءة الصورة من جديد">
            <Icon name={busy ? 'loader-2' : 'refresh-cw'} size={13} className={busy ? 'animate-spin' : ''} />
          </button>
        )}
        {onClose && (
          <button type="button" className="ocr-panel__tool" onClick={onClose} aria-label="إخفاء النص">
            <Icon name="x" size={13} />
          </button>
        )}
      </header>

      <div className="ocr-panel__body custom-scrollbar">
        {row.ocrStatus === 'done' && blocks.length ? (
          blocks.map((block, i) =>
            block.type === 'table' ? (
              <div key={i} className="ocr-table-wrap custom-scrollbar" dir={block.dir}>
                <table className="ocr-table">
                  <thead>
                    <tr>
                      {block.header.map((cell, j) => (
                        <th key={j}>{cell}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {block.rows.map((cells, r) => (
                      <tr key={r}>
                        {cells.map((cell, j) => (
                          <td key={j}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p key={i} dir={block.dir} className="ocr-paragraph">
                {block.text}
              </p>
            ),
          )
        ) : (
          <p className="py-4 text-center text-xs opacity-80">
            {meta && meta.busy
              ? 'تُقرأ الصورة الآن على خادم المستشفى — يظهر النص هنا تلقائياً خلال لحظات.'
              : row.ocrStatus === 'failed'
                ? 'تعذرت قراءة هذه الصورة. يمكنك طلب قراءتها من جديد.'
                : 'لم يُعثر على نص مكتوب في هذه الصورة.'}
          </p>
        )}
      </div>
    </section>
  );
}
